import { useEthereal } from "@/features/ethereals/useEthereal";
import styles from "./CollectionCard.module.scss";
import { useTokenMetadata } from "@/features/ethereals/useTokenMetadata";
import { LuGhost } from "react-icons/lu";
import { Form } from "formik";
import YupNative from "@/components/layout/form/yup";
import FormikInput from "@/components/layout/form/formik/FormikInput";
import { Web3Button } from "@/components/layout/Web3Button";
import { FormikWrapped } from "@/components/layout/form/formik/FormikForm";
import { blessnetMainnet, blessnetSepolia } from "@/config/bless-chain";
import { formatEther } from "viem";
import dayjs from "dayjs";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Tooltip } from "antd";
import LoadingIndicatorCard from "@/features/mod-perms/components/cards/loading-card/loading-indicator-card";
import { isMainnet } from "@/constants/flags.constant";

// Status types for the card
type EtherealStatus =
  | "not_mirrored"
  | "pending"
  | "syncing"
  | "active"
  | "unfunded"
  | "not_found";

interface CollectionCardProps {
  chainId: number;
  contractAddress: string;
  onCreateEthereal?: () => void;
}

export default function CollectionCard({
  chainId,
  contractAddress,
}: CollectionCardProps) {
  const tokenMetadataQuery = useTokenMetadata({
    chainId,
    contractAddress,
  });

  const { etherealQuery, handleSubmit } = useEthereal({
    chainId,
    contractAddress,
  });

  if (etherealQuery.isLoading || tokenMetadataQuery.isLoading) {
    return <LoadingIndicatorCard />;
  }

  if (
    etherealQuery.error &&
    // @ts-ignore that this is prob an ApiError
    etherealQuery.error.statusCode !== 404
  ) {
    return (
      <div>
        There was an error loading your ethereal: {etherealQuery.error.message}
      </div>
    );
  }
  // Determine the current status of the card
  const getEtherealStatus = (): EtherealStatus => {
    if (tokenMetadataQuery.error) return "not_found";
    // @ts-ignore that we throw an ApiError
    if (etherealQuery.error?.statusCode === 404) return "not_mirrored";
    return etherealQuery.data?.status;
  };

  const etherealStatus = getEtherealStatus();

  // Not found state
  if (etherealStatus === "not_found") {
    return (
      <div className={styles.card}>
        <div className={styles.imageContainer}>
          <div className={styles.placeholderImage}>
            <LuGhost style={{ width: "50%", height: "auto" }} />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.header}>
            <h2 className={styles.title}>Not found</h2>
          </div>
          <div className={styles.status}>
            <div className={`${styles.statusDot} ${styles.statusError}`} />
            <span className={styles.statusText}>
              This doesn&apos;t seem to be a valid token contract address
            </span>
          </div>
          <p className={styles.description}>
            Only ERC20, ERC721 and ERC1155 are supported for ethereals
          </p>
        </div>
      </div>
    );
  }

  // Get status text and color based on status
  const getStatusInfo = () => {
    switch (etherealStatus) {
      case "not_mirrored":
        return {
          color: "purple",
          text: "This collection is not yet mirrored on blessnet",
        };
      case "pending":
        return {
          color: "other",
          text: "Initializing",
        };
      case "syncing":
        return {
          color: "green",
          // TODO: format date
          text: `sync in progress`,
        };
      case "active":
        return {
          color: "green",
          // TODO: format date
          text: `synced on ${dayjs(
            Date.parse(etherealQuery.data?.lastSyncedAt),
          ).format("YYYY-MM-DD HH:mm:ss")}`,
        };
      case "unfunded":
        return {
          color: "red",
          text: `Out of funds! (Latest sync: ${
            etherealQuery.data?.lastSyncedAt
              ? dayjs(Date.parse(etherealQuery.data?.lastSyncedAt)).format(
                  "YYYY-MM-DD HH:mm:ss",
                )
              : "unknown"
          })`,
        };
      default:
        return {
          color: "purple",
          text: "Unknown status",
        };
    }
  };

  const statusInfo = getStatusInfo();

  const imageUrl =
    tokenMetadataQuery.data?.openSeaMetadata?.imageUrl || "/placeholder.png";

  const submissionChainId = isMainnet ? blessnetMainnet.id : blessnetSepolia.id;
  return (
    <div className={styles.card}>
      {/* Image Section */}
      <div className={styles.imageContainer}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt={tokenMetadataQuery.data?.name || "Token"}
          style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover" }}
        />
      </div>

      {/* Content Section */}
      <div className={styles.content}>
        {/* Title and NFT Badge */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {tokenMetadataQuery.data?.name || "Unknown Token"}
          </h2>
          <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
            {etherealStatus !== "not_mirrored" && (
              <Tooltip title="View on explorer">
                <a
                  href={
                    etherealQuery.data?.chainType === "sepolia"
                      ? // we point to /address/ and not /token/ because it takes a while for the scanner to have that page up
                        `https://blessnet-sepolia-testnet.explorer.caldera.xyz/address/${etherealQuery.data?.ethereal}`
                      : `https://scan.bless.net/address/${etherealQuery.data?.ethereal}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {" "}
                  <FaExternalLinkAlt />
                </a>
              </Tooltip>
            )}
            <span className={styles.nftBadge}>NFT</span>
          </div>
        </div>

        {/* Supply Numbers */}
        <div className={styles.supply}>
          <div>
            <span>
              {(tokenMetadataQuery.data?.owners?.length || 0).toLocaleString()}
            </span>
            <span> Holders</span>
          </div>
          <div>
            <span>
              {Number(
                tokenMetadataQuery.data?.totalSupply || 0,
              ).toLocaleString()}
            </span>
            <span> Tokens</span>
          </div>
        </div>

        <div
          // yep looks crazy but i basically simply don't want the grid-col rule to apply
          className={etherealStatus === "not_mirrored" ? "" : styles.data}
        >
          {/* Status Indicator */}
          <div className={styles.status}>
            <div
              className={`${styles.statusDot} ${
                statusInfo.color === "green"
                  ? styles.statusSuccess
                  : statusInfo.color === "red"
                  ? styles.statusError
                  : styles.statusPurple
              }`}
            />
            <span className={styles.statusText}>{statusInfo.text}</span>
          </div>

          {/* Remaining balance for in-sync state */}
          {etherealStatus === "active" ||
            (etherealStatus === "pending" && (
              <div className={styles.remainingBalance}>
                {etherealQuery.data.balance &&
                  formatEther(BigInt(etherealQuery.data.balance)).slice(
                    0,
                    6,
                  )}{" "}
                BLESS remaining
              </div>
            ))}
        </div>
        {/* Funding Input */}
        <FormikWrapped
          onSubmit={handleSubmit}
          initialValues={{
            fundingAmount: 0.1,
            unit: "eth",
          }}
          validateOnChange
          validationSchema={YupNative.object().shape({
            fundingAmount: YupNative.string()
              .eth()
              .minEth(0.1, "The minimum required funding is 0.1 BLESS")
              .required("Required"),
          })}
        >
          {({ isSubmitting, isValid, values }) => (
            <Form>
              <div className={styles.fundingInput}>
                <FormikInput
                  label={
                    etherealStatus === "not_mirrored"
                      ? "Initial funds"
                      : "Top up amount"
                  }
                  name="fundingAmount"
                  description="Funds will be used to keep the ethereal up to date"
                  type="number"
                  disabled={isSubmitting}
                  suffix="BLESS"
                  showBalance
                  chainId={submissionChainId}
                />
                <Web3Button
                  chainId={submissionChainId}
                  size="small"
                  htmlType="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                >
                  {etherealStatus === "not_mirrored"
                    ? "Create ethereal"
                    : "Add Funds"}
                </Web3Button>
              </div>
            </Form>
          )}
        </FormikWrapped>
      </div>
    </div>
  );
}
